import React, { useEffect, useState } from "react";
import {
  Wallet,
  TrendingUp,
  Calendar,
  Target,
  Eye,
  EyeOff,
  ArrowUpRight,
  Loader2,
} from "lucide-react";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { api } from "@/lib/api";
import { formatBRL, formatDateTime } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

function StatCard({ label, value, sub, icon: Icon, testid, accent = false, onToggle, hidden }) {
  return (
    <Card
      data-testid={testid}
      className={
        "card-hover relative overflow-hidden " +
        (accent ? "bg-primary/5 border-primary/30" : "bg-card border-border")
      }
    >
      <CardContent className="p-5 md:p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div
            className={
              "w-9 h-9 rounded-lg grid place-items-center " +
              (accent ? "bg-primary/20 text-primary" : "bg-secondary text-muted-foreground")
            }
          >
            <Icon className="w-4 h-4" />
          </div>
          {onToggle && (
            <button
              onClick={onToggle}
              className="text-muted-foreground hover:text-foreground"
              data-testid="toggle-balance-visibility"
            >
              {hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          )}
        </div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
        <p className={"font-display font-bold font-mono-num tracking-tight text-2xl md:text-3xl " + (accent ? "text-primary" : "")}>
          {hidden ? "••••••" : value}
        </p>
        {sub && <p className="text-xs text-muted-foreground mt-2">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [balanceHidden, setBalanceHidden] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await api.get("/stats/dashboard");
        if (mounted) setStats(res.data);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 15000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, []);

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const monthlyPct = Math.min(100, Math.round((stats.monthly_progress || 0) * 100));
  const weeklyPct = Math.min(100, Math.round((stats.weekly_progress || 0) * 100));

  const chartData = (stats.daily_series || []).map((d) => ({
    date: d.date.slice(5).replace("-", "/"),
    total: d.total / 100,
  }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-start md:items-center justify-between flex-col md:flex-row gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">
            Bem-vindo de volta
          </p>
          <h1 className="font-display text-3xl md:text-4xl font-bold">Dashboard</h1>
          {stats.simulation_mode && (
            <Badge
              variant="outline"
              className="mt-2 border-amber-500/40 text-amber-400 bg-amber-500/10"
              data-testid="simulation-badge"
            >
              Modo Simulação Ativo
            </Badge>
          )}
        </div>
        <Button asChild className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
          <Link to="/gerar-pix" data-testid="cta-create-pix">
            <ArrowUpRight className="w-4 h-4 mr-1" />
            Gerar cobrança PIX
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <StatCard
          label="Saldo na conta"
          value={formatBRL(stats.balance_cents)}
          sub={stats.balance_source === "woovi" ? "Fonte: Woovi" : "Calculado (vendas - saques)"}
          icon={Wallet}
          testid="stat-balance"
          accent
          onToggle={() => setBalanceHidden((v) => !v)}
          hidden={balanceHidden}
        />
        <StatCard
          label="Vendido hoje"
          value={formatBRL(stats.total_today_cents)}
          sub="Atualizado agora"
          icon={TrendingUp}
          testid="stat-today"
        />
        <StatCard
          label="Semana"
          value={formatBRL(stats.total_week_cents)}
          sub={`Faltam ${formatBRL(stats.weekly_remaining_cents)} p/ meta`}
          icon={Calendar}
          testid="stat-week"
        />
        <StatCard
          label="Mês"
          value={formatBRL(stats.total_month_cents)}
          sub={`Faltam ${formatBRL(stats.monthly_remaining_cents)} p/ meta`}
          icon={Target}
          testid="stat-month"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 bg-card border-border" data-testid="chart-card">
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="font-display text-lg">Vendas dos últimos 30 dias</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Valor total recebido por dia (R$)
              </p>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 8, left: -15, bottom: 0 }}>
                  <defs>
                    <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(158 96% 48%)" stopOpacity={0.55} />
                      <stop offset="100%" stopColor="hsl(158 96% 48%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="hsl(240 4% 18%)" strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: "hsl(240 5% 65%)", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: "hsl(240 5% 65%)", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={50}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(240 6% 10%)",
                      border: "1px solid hsl(240 4% 16%)",
                      borderRadius: 8,
                      color: "hsl(0 0% 98%)",
                      fontSize: 12,
                    }}
                    formatter={(v) => [`R$ ${Number(v).toFixed(2)}`, "Total"]}
                  />
                  <Area
                    type="monotone"
                    dataKey="total"
                    stroke="hsl(158 96% 48%)"
                    strokeWidth={2}
                    fill="url(#chartGrad)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="bg-card border-border" data-testid="goal-monthly">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Meta do mês</p>
                <span className="text-xs font-mono-num text-primary">{monthlyPct}%</span>
              </div>
              <p className="font-display font-bold text-xl mb-1 font-mono-num">
                {formatBRL(stats.total_month_cents)}
              </p>
              <p className="text-xs text-muted-foreground mb-3">
                de {formatBRL(stats.monthly_goal_cents)}
              </p>
              <Progress value={monthlyPct} className="h-2" />
            </CardContent>
          </Card>
          <Card className="bg-card border-border" data-testid="goal-weekly">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Meta da semana</p>
                <span className="text-xs font-mono-num text-primary">{weeklyPct}%</span>
              </div>
              <p className="font-display font-bold text-xl mb-1 font-mono-num">
                {formatBRL(stats.total_week_cents)}
              </p>
              <p className="text-xs text-muted-foreground mb-3">
                de {formatBRL(stats.weekly_goal_cents)}
              </p>
              <Progress value={weeklyPct} className="h-2" />
            </CardContent>
          </Card>
        </div>
      </div>

      <Card className="bg-card border-border" data-testid="recent-transactions">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="font-display text-lg">Últimas vendas recebidas</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">As 5 mais recentes</p>
          </div>
          <Button asChild variant="ghost" size="sm">
            <Link to="/vendas" data-testid="view-all-sales">
              Ver todas
              <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          {(stats.recent_transactions || []).length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Nenhuma venda recebida ainda. Crie sua primeira cobrança PIX.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {stats.recent_transactions.map((t) => (
                <div
                  key={t.id}
                  className="flex items-center justify-between py-3 gap-3"
                  data-testid={`recent-row-${t.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">
                      {t.payer?.name || "Pagador PIX"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t.payer?.cpf || "-"} · {formatDateTime(t.paid_at)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono-num font-semibold text-primary">
                      + {formatBRL(t.value)}
                    </p>
                    <Badge variant="outline" className="mt-1 text-[10px] border-primary/30 text-primary bg-primary/10">
                      PAGO
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
